import { useCallback, useEffect, useMemo } from "preact/hooks";
import { route } from "preact-router";
import { useData } from "../hooks/useData";
import { useTimeRange, type TimeRange } from "../hooks/useTimeRange";
import {
  fetchClients,
  fetchClientHistory,
  fetchStatus,
} from "../utils/api";
import {
  formatBps,
  formatBytes,
  formatTimeAgo,
} from "../utils/format";
import { StatusBadge } from "../components/StatusBadge";
import { TimeRangePicker } from "../components/TimeRangePicker";
import { SkeletonLoader } from "../components/SkeletonLoader";
import { ErrorMessage } from "../components/ErrorMessage";
import { LastUpdated } from "../components/LastUpdated";
import { ClientBandwidthChart } from "../components/charts/ClientBandwidthChart";
import "./Clients.css";

export default function Clients() {
  const { range, setRange } = useTimeRange();

  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [window.location.search]
  );
  const selectedName = queryParams.get("name") ?? "";

  const fetchClientsCb = useCallback(() => fetchClients(), []);
  const {
    data: clientsData,
    loading: clientsLoading,
    error: clientsError,
    refresh: refreshClients,
  } = useData(fetchClientsCb);

  const fetchStatusCb = useCallback(() => fetchStatus(), []);
  const {
    data: status,
    loading: statusLoading,
    error: statusError,
    refresh: refreshStatus,
  } = useData(fetchStatusCb);

  const clientOptions = clientsData?.clients ?? [];

  useEffect(() => {
    if (!selectedName && clientOptions.length > 0) {
      route(`/clients?name=${encodeURIComponent(clientOptions[0].name)}`);
    }
  }, [selectedName, clientOptions]);

  const fetchHistoryCb = useCallback(
    () => fetchClientHistory(selectedName, range),
    [selectedName, range]
  );
  const {
    data: history,
    loading: historyLoading,
    error: historyError,
    refresh: refreshHistory,
    lastUpdated: historyLastUpdated,
  } = useData(fetchHistoryCb);

  const selectedClient = clientOptions.find((c) => c.name === selectedName);
  const netwatch = status?.netwatch.find(
    (n) => n.name === selectedClient?.name
  );
  const queue = status?.queues.find((q) => q.name === selectedClient?.name);

  const handleSelect = (e: Event) => {
    const target = e.currentTarget as HTMLSelectElement;
    const name = target.value;
    if (name) {
      route(`/clients?name=${encodeURIComponent(name)}`);
    }
  };

  const handleRangeChange = (r: string) => setRange(r as TimeRange);

  const usage = useMemo(() => {
    const data = history?.data ?? [];
    if (data.length === 0) return null;

    let totalDown = 0;
    let totalUp = 0;
    let peakDown = 0;
    let peakUp = 0;

    for (let i = 0; i < data.length; i++) {
      const curr = data[i];
      const next = data[i + 1];
      const interval = next
        ? (new Date(next.timestamp).getTime() -
            new Date(curr.timestamp).getTime()) /
          1000
        : i > 0
        ? (new Date(curr.timestamp).getTime() -
            new Date(data[i - 1].timestamp).getTime()) /
          1000
        : 300;

      totalDown += curr.rate_download * interval;
      totalUp += curr.rate_upload * interval;
      peakDown = Math.max(peakDown, curr.rate_download);
      peakUp = Math.max(peakUp, curr.rate_upload);
    }

    return { totalDown, totalUp, peakDown, peakUp };
  }, [history]);

  const lastHistoryTimestamp =
    history && history.data.length > 0
      ? history.data[history.data.length - 1].timestamp
      : null;

  return (
    <div class="clients">
      <h1 class="clients__title">Detail Client</h1>

      {(clientsError || statusError) && (
        <ErrorMessage
          message={(clientsError ?? statusError)?.message ?? "Gagal memuat data"}
          onRetry={() => {
            refreshClients();
            refreshStatus();
          }}
        />
      )}

      <section class="clients__selector card">
        <label class="clients__label" htmlFor="client-select">
          Pilih Client
        </label>
        {clientsLoading ? (
          <SkeletonLoader type="card" />
        ) : (
          <select
            id="client-select"
            class="clients__select"
            value={selectedName}
            onChange={handleSelect}
          >
            <option value="" disabled>
              -- Pilih client --
            </option>
            {clientOptions.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.target})
              </option>
            ))}
          </select>
        )}
      </section>

      {selectedClient && (
        <section class="clients__info card">
          {statusLoading ? (
            <SkeletonLoader type="card" />
          ) : (
            <>
              <div class="clients__info-header">
                <div>
                  <h2 class="clients__name">{selectedClient.name}</h2>
                  <p class="clients__target">{selectedClient.target}</p>
                </div>
                <StatusBadge
                  status={
                    netwatch?.status === "up" || netwatch?.status === "down"
                      ? netwatch.status
                      : "unknown"
                  }
                />
              </div>
              <div class="clients__info-grid">
                <div>
                  <p class="clients__info-label">Download</p>
                  <p class="clients__info-value">
                    {formatBps(queue?.rate_download ?? 0)}
                  </p>
                </div>
                <div>
                  <p class="clients__info-label">Upload</p>
                  <p class="clients__info-value">
                    {formatBps(queue?.rate_upload ?? 0)}
                  </p>
                </div>
                <div>
                  <p class="clients__info-label">Max Download</p>
                  <p class="clients__info-value">
                    {formatBps(queue?.max_limit_download ?? 0)}
                  </p>
                </div>
                <div>
                  <p class="clients__info-label">Max Upload</p>
                  <p class="clients__info-value">
                    {formatBps(queue?.max_limit_upload ?? 0)}
                  </p>
                </div>
              </div>
              {netwatch && (
                <p class="clients__since">
                  Status terakhir: {formatTimeAgo(netwatch.last_update)}
                </p>
              )}
            </>
          )}
        </section>
      )}

      {selectedClient && (
        <section class="clients__section card">
          <div class="clients__section-header">
            <h2 class="clients__section-title">
              Bandwidth - {selectedClient.name}
            </h2>
            <div class="clients__section-actions">
              <LastUpdated timestamp={lastHistoryTimestamp} />
              <TimeRangePicker value={range} onChange={handleRangeChange} />
            </div>
          </div>
          {historyError && (
            <ErrorMessage
              message={historyError.message}
              onRetry={refreshHistory}
            />
          )}
          <ClientBandwidthChart
            data={history?.data ?? []}
            clientName={selectedClient.name}
            loading={historyLoading}
            error={historyError?.message}
          />
        </section>
      )}

      {selectedClient && usage && (
        <section class="clients__section card">
          <h2 class="clients__section-title">Ringkasan Penggunaan ({range})</h2>
          <div class="clients__usage-grid">
            <div class="clients__usage-item">
              <p class="clients__usage-label">Total Download</p>
              <p class="clients__usage-value">{formatBytes(usage.totalDown)}</p>
            </div>
            <div class="clients__usage-item">
              <p class="clients__usage-label">Total Upload</p>
              <p class="clients__usage-value">{formatBytes(usage.totalUp)}</p>
            </div>
            <div class="clients__usage-item">
              <p class="clients__usage-label">Peak Download</p>
              <p class="clients__usage-value">{formatBps(usage.peakDown)}</p>
            </div>
            <div class="clients__usage-item">
              <p class="clients__usage-label">Peak Upload</p>
              <p class="clients__usage-value">{formatBps(usage.peakUp)}</p>
            </div>
          </div>
        </section>
      )}

      <footer class="clients__footer">
        <LastUpdated timestamp={historyLastUpdated?.toISOString() ?? null} />
      </footer>
    </div>
  );
}
